import React, { useState } from 'react';
import { 
  LinkOutlined, 
  PhoneOutlined, 
  FileImageOutlined, 
  LeftOutlined, 
  RightOutlined 
} from '@ant-design/icons';

const WhatsAppUniversalTemplatePreview = ({ apiResponse, variables = [] }) => {
  const components = apiResponse?.data?.components || [];
  
  // Track the current card index for templates that contain a CAROUSEL layout
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Helper function to safely inject real-time inputs into {{1}}, {{2}} template tags
  const injectVariables = (text) => {
    if (!text) return "";
    let formattedText = text;
    variables.forEach((val, index) => {
      const matchTag = `{{${index + 1}}}`;
      formattedText = formattedText.replace(matchTag, val || `[${matchTag}]`);
    });
    return formattedText;
  };

  // Extract core component nodes for standard templates
  const header = components.find(c => c.type === 'HEADER');
  const body = components.find(c => c.type === 'BODY');
  const footer = components.find(c => c.type === 'FOOTER');
  const buttonsNode = components.find(c => c.type === 'BUTTONS');
  const carousel = components.find(c => c.type === 'CAROUSEL');

  // Helper to render quick-action or link buttons at the bottom of a card/bubble
  const renderButtons = (buttonsList) => {
    if (!buttonsList || buttonsList.length === 0) return null;
    return (
      <div style={styles.buttonWrapper}>
        {buttonsList.map((btn, index) => (
          <div key={index} style={styles.actionButton}>
            {btn.type === 'URL' ? <LinkOutlined style={styles.buttonIcon} /> : 
             btn.type === 'PHONE_NUMBER' ? <PhoneOutlined style={styles.buttonIcon} /> : null}
            <span style={styles.buttonText}>{btn.text}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.whatsappContainer}>
      <div style={styles.phoneLayout}>
        
        {/* --- TYPE 1: STANDARD SINGLE CHAT BUBBLE LAYOUT (Templates 1, 2, 4, 5) --- */}
        {body && (
          <div style={styles.messageBubble}>
            {/* Image Header Slot (Template 5) */}
            {header && header.format === 'IMAGE' && (
              <div style={styles.mediaPlaceholder}>
                <FileImageOutlined style={styles.mediaIcon} />
              </div>
            )}

            {/* Bold Text Header Slot (Templates 1 & 2) */}
            {header && header.format === 'TEXT' && (
              <div style={styles.headerText}>{header.text}</div>
            )}

            {/* Main Message Body Text */}
            <p style={styles.bodyText}>{injectVariables(body.text)}</p>

            {/* Footer Text (Templates 1, 2, 5) */}
            {footer && <div style={styles.footerText}>{footer.text}</div>}

            <span style={styles.timestamp}>11:32 PM</span>
          </div>
        )}

        {/* Standard Single Bubble Buttons (Templates 2 & 5) */}
        {!carousel && buttonsNode && renderButtons(buttonsNode.buttons)}


        {/* --- TYPE 2: MULTI-CARD INTERACTIVE CAROUSEL LAYOUT (Template 3) --- */}
        {carousel && carousel.cards && (
          <div style={styles.carouselSection}>
            
            {/* Left Carousel Navigation Trigger Arrow */}
            <button 
              disabled={activeCardIndex === 0}
              onClick={() => setActiveCardIndex(p => p - 1)}
              style={{ ...styles.navButton, left: -10, opacity: activeCardIndex === 0 ? 0.4 : 1 }}
            >
              <LeftOutlined />
            </button>

            {/* Active Card Body Wrapper Box */}
            {(() => {
              const currentCard = carousel.cards[activeCardIndex];
              if (!currentCard) return null;

              const cardHeader = currentCard.components?.find(c => c.type === 'HEADER');
              const cardBody = currentCard.components?.find(c => c.type === 'BODY');
              const cardButtons = currentCard.components?.find(c => c.type === 'BUTTONS');

              return (
                <div style={styles.carouselCard}>
                  {/* Card Media Header Frame */}
                  {cardHeader && cardHeader.format === 'IMAGE' && (
                    <div style={styles.carouselMediaSlot}>
                      <FileImageOutlined style={styles.mediaIcon} />
                    </div>
                  )}

                  {/* Card Message Copy Text Context */}
                  {cardBody && (
                    <div style={styles.cardContentPadding}>
                      <p style={styles.carouselCardBodyText}>{injectVariables(cardBody.text)}</p>
                    </div>
                  )}

                  {/* Individual Card Contextual Actions */}
                  {cardButtons && renderButtons(cardButtons.buttons)}
                </div>
              );
            })()}

            {/* Right Carousel Navigation Trigger Arrow */}
            <button 
              disabled={activeCardIndex === carousel.cards.length - 1}
              onClick={() => setActiveCardIndex(p => p + 1)}
              style={{ ...styles.navButton, right: -10, opacity: activeCardIndex === carousel.cards.length - 1 ? 0.4 : 1 }}
            >
              <RightOutlined />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

// Precise styling matching Meta UI design patterns
const styles = {
  whatsappContainer: {
    background: '#f3dab5ff', // Native WhatsApp conversation backdrop color hex
    padding: '24px 16px',
    borderRadius: '12px',
    maxWidth: '380px',
    boxSizing: 'border-box',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
  },
  phoneLayout: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%'
  },
  messageBubble: {
    background: '#ffffff',
    padding: '10px 12px 20px 12px',
    borderRadius: '8px',
    borderTopLeftRadius: '0px',
    boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
    position: 'relative',
    overflow: 'hidden',
    width: '90%'
  },
  headerText: {
    fontWeight: '700',
    fontSize: '14.5px',
    color: '#111b21',
    marginBottom: '5px'
  },
  bodyText: {
    margin: 0,
    fontSize: '14.2px',
    color: '#111b21',
    lineHeight: '1.42',
    whiteSpace: 'pre-wrap'
  },
  footerText: {
    fontSize: '12px',
    color: '#667781',
    marginTop: '6px'
  },
  mediaPlaceholder: {
    background: '#cbd5e1',
    height: '160px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px'
  },
  mediaIcon: {
    fontSize: '40px',
    color: '#94a3b8'
  },
  timestamp: {
    position: 'absolute',
    bottom: '4px',
    right: '8px',
    fontSize: '10.5px',
    color: '#667781'
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
    width: '90%'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '11px',
    background: '#ffffff',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box'
  },
  buttonIcon: {
    fontSize: '14px',
    color: '#008069'
  },
  buttonText: {
    fontSize: '14px',
    color: '#008069',
    fontWeight: '500'
  },
  /* --- Carousel Specific Layout Components Styles --- */
  carouselSection: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '90%',
    marginTop: '6px'
  },
  carouselCard: {
    background: '#ffffff',
    borderRadius: '10px',
    overflow: 'hidden',
    width: '100%',
    boxShadow: '0 1px 1px rgba(0,0,0,0.15)',
    borderTopLeftRadius: '0px'
  },
  carouselMediaSlot: {
    background: '#cbd5e1',
    height: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  cardContentPadding: {
    padding: '12px 12px'
  },
  carouselCardBodyText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '500',
    color: '#111b21',
    lineHeight: '1.35'
  },
  navButton: {
    position: 'absolute',
    zIndex: 10,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#ffffff',
    border: 'none',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  }
};

export default WhatsAppUniversalTemplatePreview;